import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Divider,
  Flex,
  Heading,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Show } from '@pelicargo/ui';
import { getPrettyAccountWebhookEvent } from '@pelicargo/utils';
import { useEffect, useState } from 'react';

import { TrpcOutputs } from '../../config/trpc';
import { useAccountWebhooks } from '../../hooks/accountWebhook/useAccountWebhooks';
import { AlertModal } from '../AlertModal/AlertModal';
import { AccountWebhookModal } from './AccountWebhookModal';

type AccountWebhook = TrpcOutputs['getAccountWebhooksByAccount'][number];
type WebhookTestResult = TrpcOutputs['testAccountWebhook'];

type Props = {
  accountId: number;
  isDrawerOpen: boolean;
};

export const AccountWebhooksSection = ({ accountId, isDrawerOpen }: Props) => {
  const toast = useToast();
  const {
    webhooks = [],
    isLoading,
    createWebhook,
    updateWebhook,
    deleteWebhook,
    testWebhook,
    isCreating,
    isUpdating,
    isDeleting,
  } = useAccountWebhooks(accountId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<AccountWebhook>();
  const [deletingWebhook, setDeletingWebhook] = useState<AccountWebhook>();
  const [testingWebhookId, setTestingWebhookId] = useState<number>();
  const [testResults, setTestResults] = useState<Record<number, WebhookTestResult>>({});

  useEffect(() => {
    if (isDrawerOpen) return;
    setTestResults({});
    setTestingWebhookId(undefined);
  }, [isDrawerOpen]);

  const openAddModal = () => {
    setEditingWebhook(undefined);
    setIsModalOpen(true);
  };

  const openEditModal = (webhook: AccountWebhook) => {
    setEditingWebhook(webhook);
    setIsModalOpen(true);
  };

  const closeWebhookModal = () => {
    setIsModalOpen(false);
    setEditingWebhook(undefined);
  };

  const handleTest = async (webhook: AccountWebhook) => {
    setTestingWebhookId(webhook.id);
    try {
      const result = await testWebhook({ id: webhook.id });
      setTestResults((current) => ({ ...current, [webhook.id]: result }));
    } catch (error) {
      setTestResults((current) => ({
        ...current,
        [webhook.id]: { ok: false, duration_ms: 0, error: error?.message ?? 'Delivery failed' },
      }));
    } finally {
      setTestingWebhookId(undefined);
    }
  };

  const handleDelete = async () => {
    if (!deletingWebhook) return;

    try {
      await deleteWebhook({ id: deletingWebhook.id });
      setDeletingWebhook(undefined);
    } catch (error) {
      toast({
        title: 'Webhook could not be deleted',
        description: error?.message,
        status: 'error',
      });
    }
  };

  return (
    <Stack spacing="4">
      <Flex justify="space-between" align="start" gap="4">
        <Stack spacing="1">
          <Heading variant="h5">Webhooks</Heading>
          <Text textStyle="caption">Signed with X-Pelicargo-Signature. Verify with /v3/meta/pubkey.</Text>
        </Stack>
        <Button colorScheme="brand" onClick={openAddModal} flexShrink={0}>
          Add webhook
        </Button>
      </Flex>

      <Show if={isLoading}>
        <Spinner alignSelf="center" />
      </Show>
      <Show if={!isLoading && webhooks.length === 0}>
        <Text color="gray.500">No webhooks yet.</Text>
      </Show>
      <Show if={!isLoading && webhooks.length > 0}>
        <Stack divider={<Divider />} spacing="0">
          {webhooks.map((webhook) => {
            const result = testResults[webhook.id];
            const isSending = testingWebhookId === webhook.id;
            const resultDetail =
              result?.status ?? (result as Extract<WebhookTestResult, { ok: false }> | undefined)?.error;

            return (
              <Card key={webhook.id} variant="unstyled">
                <CardBody px="0" py="3">
                  <Stack spacing="2">
                    <Flex justify="space-between" align="start" gap="4">
                      <Stack spacing="1" minW="0">
                        <Text fontFamily="monospace" fontSize="sm" noOfLines={1} title={webhook.uri}>
                          {webhook.uri}
                        </Text>
                        <Badge alignSelf="start" colorScheme="blue">
                          {getPrettyAccountWebhookEvent(webhook.event)}
                        </Badge>
                      </Stack>
                      <ButtonGroup size="sm" flexShrink={0}>
                        <Button variant="outline" onClick={() => handleTest(webhook)} isLoading={isSending}>
                          Send test
                        </Button>
                        <Button variant="ghost" onClick={() => openEditModal(webhook)}>
                          Edit
                        </Button>
                        <Button variant="ghost" colorScheme="red" onClick={() => setDeletingWebhook(webhook)}>
                          Delete
                        </Button>
                      </ButtonGroup>
                    </Flex>
                    <Show if={isSending}>
                      <Badge alignSelf="start" colorScheme="blue">
                        Sending…
                      </Badge>
                    </Show>
                    <Show if={!isSending && !!result}>
                      <Badge alignSelf="start" colorScheme={result?.ok ? 'green' : 'red'}>
                        {result?.ok ? 'Delivered' : 'Failed'} · {resultDetail} · {result?.duration_ms}ms
                      </Badge>
                    </Show>
                  </Stack>
                </CardBody>
              </Card>
            );
          })}
        </Stack>
      </Show>

      <AccountWebhookModal
        accountId={accountId}
        webhook={editingWebhook}
        isOpen={isModalOpen}
        onClose={closeWebhookModal}
        createWebhook={createWebhook}
        updateWebhook={updateWebhook}
        isCreating={isCreating}
        isUpdating={isUpdating}
      />
      <AlertModal
        isOpen={!!deletingWebhook}
        onClose={() => setDeletingWebhook(undefined)}
        title="Delete this webhook?"
        message={`${deletingWebhook?.uri ?? 'This endpoint'} will stop receiving events.`}
        btnCancel="Cancel"
        btnDelete="Delete"
        isDeleting={isDeleting}
        handleConfirm={handleDelete}
      />
    </Stack>
  );
};
